import { useLayoutEffect, useState } from 'react';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { EIconSize } from 'utils/constants/app/ui';
import {
  competitions,
  PREMATCH_GAME_TYPE_FOR_SWARM,
  regions
} from 'utils/constants/sportsbook/data-fields';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { getMountPath } from 'utils/generic/route-manipulation';
import RidGenerator from 'utils/swarm/rid-generator';
import { Region } from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import { EventCollapse } from 'components/EventCollapse';
import { GlobalIcon } from 'components/GlobalIcon';
import { UrlWrapper } from 'components/Shared/UrlWrapper';
import { RegionSkeleton } from 'components/UI/Skeletons/RegionSkeleton';
import { CompetitionFavoriteMatchesCountIndicator } from 'newcomponents/Shared/sportsbook/CompetitionFavoriteMatchesCountIndicator';
import { CompetitionsFavorite } from 'newcomponents/Shared/sportsbook/CompetitionsFavorite';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';

type TRegionsCollapseBySportProps = {
  sportAlias: string;
  clickHandler: (alias: string) => void;
};

export const RegionsCollapseBySport = ({
  sportAlias,
  clickHandler
}: TRegionsCollapseBySportProps) => {
  const [regionsData, setRegionsData] = useState<Region[] | null>(null);
  const { path } = useSbRouteMatch();

  useLayoutEffect(() => {
    const rid = RidGenerator.gForSubscribe(
      `${sportAlias.toUpperCase()}_REGIONS`
    );

    const where: Record<string, any> = {
      sport: {
        alias: sportAlias
      },
      game: PREMATCH_GAME_TYPE_FOR_SWARM
    };

    const relations: Record<string, any> = {
      ...regions,
      ...competitions
    };

    const callback = (data: { region: Record<string, Region> }) => {
      if (data?.region) {
        setRegionsData(sortByOrder(Object.values(data.region)));
      }
    };

    executeCommand(rid, relations, where, callback, true, callback);

    return () => {
      unsubscribeCommand(rid);
    };
  }, []);

  const getRouteForRedirect = ({
    regionAlias,
    compId
  }: {
    regionAlias: string;
    compId: number;
  }) => {
    return `${getMountPath(path)}/${
      SpringConfigs.SPORTSBOOK_MOUNT_PATH
    }/${sportAlias}/${regionAlias}/${compId}`;
  };

  if (!regionsData) {
    return (
      <RegionSkeleton
        regionIconSize={EIconSize._20}
        extraClassName="aTozLevelViewContainer__regionSkeleton"
      />
    );
  }

  return regionsData.map((region, index) => (
    <EventCollapse
      key={region.id}
      icon={<GlobalIcon lib="flags" name={region.alias} size={EIconSize._20} />}
      title={region.name}
      defaultActive={index === 0}
    >
      <>
        {sortByOrder(Object.values(region.competition || {})).map(comp => (
          <UrlWrapper
            url={getRouteForRedirect({
              regionAlias: region.alias,
              compId: comp.id
            })}
            className="aTozLevelViewContainer__competitionUrl"
            key={comp.id}
            onClick={() =>
              clickHandler(
                getRouteForRedirect({
                  regionAlias: region.alias,
                  compId: comp.id
                })
              )
            }
          >
            <div className="aTozLevelViewContainer__competitionNameWrapper">
              <CompetitionsFavorite
                competitionInfo={{
                  id: comp.id,
                  name: comp.name,
                  regionAlias: region.alias,
                  sportAlias
                }}
              />
              <div className="competitionWithTooltip__name">{comp.name}</div>
              <CompetitionFavoriteMatchesCountIndicator
                competitionId={comp.id}
              />
            </div>
          </UrlWrapper>
        ))}
      </>
    </EventCollapse>
  ));
};
